import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import '../../voyage-popup/src/voyage-popup';
import '../../voy-dropdown/src/voy-dropdown'
import { VoyDropdown } from '../../voy-dropdown/src/voy-dropdown';
import { AlarmType, AlarmState } from '../../../src/services/types/fleet-alarms-service.types';
import { AlarmPopupEvent, SelectAlarmReason, OtherReason, StoppageAlarmReasons, SpeedAlarmReasons, OffTrackAlarmReasons } from './types';
import "@maersk-global/mds-components-core/mc-modal";

@customElement('voy-handle-alarm-popup')
export class VoyHandleAlarmPopup extends LitElement {
    @property({type: Boolean}) shown: boolean = false;
    @property({type: String}) alarmEventType: AlarmType|null = null;
    @property({attribute: false}) private _alarmNote: string = "";
    
    @property({attribute: false}) private _dropdownData: string[] = [];
    @property({attribute: false}) private _selectedReason: string|null = null;
    @property({attribute: false}) private _showSelectAlarmReminder: boolean = false;
    @property({attribute: false}) private _showAddNoteReminder: boolean = false;
    @property({attribute: false}) private _dropdown: VoyDropdown|null = null;


    static get styles()
    {
        return [
            voyageCursorStyles,
            css`
            :host {
                font-family: MaerskText;
                font-size: 16px;
                line-height: 16px;
            }

            .container {
                width: 379px;
                display: flex;
                flex-direction: column;
                padding-top: 20px;
                position: relative;
                overflow-x: hidden !important;
            }
            .title {
                font-weight: 400;
                margin-top: 0;
                margin-bottom: 10px;
            }
            .note {
                margin-top: 20px;
            }
            voyage-popup {
                overflow: visible;
            }

            voy-dropdown {
                width: 379px;
                margin-bottom: 10px;
            }
            voy-dropdown .search-result {
                padding: 5px;
            }

            voy-dropdown[error] {
                border-color: #B80012;
            }

            .error-message {
                font-size: 14px;
                color: #B80012;
                margin-top: 0px;
            }

            textarea {
                padding: 9px;
                width: 358px;
                height: 61px;
                max-height: 300px;
                resize: vertical;
                font-family: MaerskText;
                font-size: 16px;
                border-radius: 4px;
                border-color: var(--neutrals-grey-07);
                margin-bottom: 10px;
            }
            textarea[error] {
                border-color: #B80012;
                height: 61px;

            }
            .footer {
                width: 100%;
                display: flex;
                justify-content: flex-end;
                align-items: center;
                border-top: 1px solid var(--neutrals-grey-07);
            }

            .footer-button {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 32px;
                margin-top: 12px;
                margin-bottom: 12px;
                margin-right: 15px;
                min-width: 80px;
                font-size: 14px;
                font-family: MaerskText;
                padding: 0px 12px 0px 12px;
                background: none;
                outline: none;
                border-radius: 4px;
                color: var(--neutrals-grey-02);
                border: 1px solid var(--neutrals-grey-06, grey);
            }

            .footer-button[disabled] {
                filter: opacity(0.5);
                cursor: not-allowed;
            }

            `
        ];
    }

    render()
    {
        return html`
        ${this.shown ? html`
            <mc-modal
                heading="Select Reason"
                open=${!this.shown}
                dimension="small"
                width="427px"
                height="391px"
                hiddenClose
                backdropcloseactiondisabled
                escapecloseactiondisabled
                entercloseactiondisabled

                >
                 <div class="container">
                 <p class="title" >Select reason</p>
                    <voy-dropdown .dropdownData=${this._dropdownData} @item-selected=${(e: CustomEvent) => this._onReasonSelected(e)} ?error=${this._showSelectAlarmReminder}></voy-dropdown>
                    ${this._showSelectAlarmReminder ? html`<p class="error-message">Please select reason</p>` : html ``}
                    ${this._selectedReason === OtherReason.Other ? html`<p class="title note">Write note</p>`: html`<p class="title note">Write note (optional)</p>`}
                    <textarea id="alarm-note" placeholder=${"Write your reason and/or notes"} .value=${this._alarmNote} @input=${(e: InputEvent) => this._onNoteChange(e)} ?error=${this._showAddNoteReminder} >${this._alarmNote}</textarea>
                    ${this._showAddNoteReminder ? html`<p class="error-message">Please write note</p>` : html ``}
                 </div>
                 <mc-button  id="dismiss" slot="primaryAction" appearance="primary" variant="filled" @click="${this._dismiss}" >Submit</mc-button>
                <mc-button id="cancel" focusendanchor slot="secondaryAction" appearance="neutral" variant="outlined"  @click="${this._cancel}" dialogaction="cancel">Cancel</mc-button>
            </mc-modal>
         `: html``}
         `;
    }

    firstUpdated() {
        this._dropdown = this.shadowRoot!.querySelector("voy-dropdown");
    }

    updated(changedProperties: PropertyValues) {
        if (changedProperties.has("alarmEventType")) {
            if (this.alarmEventType !== null) {
                this._setDropdownData();
            }
        }
        if (changedProperties.has("shown")) {
            if (this.shown === true) {
                this._showSelectAlarmReminder = false;
            }
        }
    }

    private _onReasonSelected(e: CustomEvent) {
        this._selectedReason = e.detail.selectedItem;
        this._showSelectAlarmReminder = false;
    }

    private _setDropdownData() {
        switch(this.alarmEventType) {
            case AlarmType.Speed:
                this._dropdownData = Object.values(SpeedAlarmReasons);
                break;
            case AlarmType.Stoppage:
                this._dropdownData = Object.values(StoppageAlarmReasons);
                break;
            case AlarmType.Crosstrack:
                this._dropdownData = Object.values(OffTrackAlarmReasons);
                break;
        }
    }

    private _cancel() {
        this._resetForm();
        this.dispatchEvent(new CustomEvent(AlarmPopupEvent.Close));
    }

    private _dismiss() {
        if (!this._selectedReason || Object.values(SelectAlarmReason).includes(this._selectedReason as SelectAlarmReason)) {
            this._showSelectAlarmReminder = true;
            return;
        }
        if(!this._isCommentOptional() && this._alarmNote === "") {
            this._showAddNoteReminder = true
            return;
        }
        this.dispatchEvent(new CustomEvent(AlarmPopupEvent.Close, {detail: {action: AlarmState.Acknowledged, selectedReason: this._selectedReason, alarmNote: this._alarmNote}}));
        this._resetForm();
    }
    private _isCommentOptional() {
        //Comment field is mandatory for Technical issue and others options
        switch (this._selectedReason) {
            case StoppageAlarmReasons.Other:
            case SpeedAlarmReasons.Other:
            case OffTrackAlarmReasons.Other:
            case StoppageAlarmReasons.ChangeEtaorDestination:
            case OffTrackAlarmReasons.Drifting:
            case OffTrackAlarmReasons.DenseTrafficManoeuvring:
            case OffTrackAlarmReasons.ChangeEtaorDestination:
            case OffTrackAlarmReasons.NavigationWarning:
                return false;
            default: return true;
        }


    }
    private _onNoteChange(evt: Event) {
        this._alarmNote = (<HTMLInputElement>evt.target).value;
        this._showSelectAlarmReminder = false;
    }

    private _resetForm() {
        this._alarmNote = "";
        this._selectedReason = null;
        this._showSelectAlarmReminder = false;
        this._showAddNoteReminder = false;
    }
};
